import type { SupportedLocale } from "staticConfiguration";
import type { ThemeOptions } from "@mui/material";
import Authorize from "core/components/Authorize";
import { GlobalLoader } from "core/components/GlobalLoader";
import { ContextVerifier } from "core/components/GlobalLoader/ContextVerifier";
import { Layout } from "core/components/Layout";
import { LayoutBypass } from "core/components/LayoutBypass";
import { PageErrorBoundary } from "core/components/PageErrorBoundary";
import { RouterLocale } from "core/components/RouterLocale";
import { SharedAppWrapper } from "core/components/SharedAppWrapper";
import { BearerTokenProvider } from "core/context/BearerTokenContext";
import { KeyContext } from "core/context/KeyContext";
import { LocaleContext } from "core/context/LocaleContext";
import { RegionContextProvider } from "core/context/RegionContextProvider";
import { SWRConfigContextProvider } from "core/context/SWRConfigContextProvider";
import type { BrandCode, RegionCode } from "core/entities";
import { CustomThemePicker } from "core/pages/LandingPage/CustomThemePicker";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useState } from "react";
import { PagePermissionChecker } from "./PagePermissionChecker";

/**
 * PitstopApp is an internal Hub application component that serves as the main
 * entry point for the Pitstop application. It handles the overall structure,
 * authentication, and routing for the application.
 */
export function PitstopApp({
  Component,
  pageProps,
  brandCode,
  availableRegions,
}: { brandCode: BrandCode; availableRegions: RegionCode[] } & Pick<
  AppProps,
  "Component" | "pageProps"
>) {
  const forceRemountKeyState = useState(0);
  const [forceRemountKey] = forceRemountKeyState;
  const {
    query: { customizeTheme },
    locale,
  } = useRouter();

  const [customTheme, setCustomTheme] = useState<ThemeOptions | null>(null);

  return (
    <KeyContext.Provider value={forceRemountKeyState}>
      <LocaleContext.Provider value={locale as SupportedLocale}>
        <SWRConfigContextProvider key={forceRemountKey}>
          <SharedAppWrapper
            brandCode={brandCode}
            customTheme={customTheme}
            availableRegions={availableRegions}
          >
            {customizeTheme === "true" && (
              <CustomThemePicker setCustomTheme={setCustomTheme} />
            )}
            <Authorize>
              <BearerTokenProvider>
                <RouterLocale />
                <LayoutBypass Component={Component} pageProps={pageProps}>
                  <RegionContextProvider>
                    <ContextVerifier>
                      <GlobalLoader>
                        <Layout>
                          <PagePermissionChecker>
                            <PageErrorBoundary>
                              <Component {...pageProps} />
                            </PageErrorBoundary>
                          </PagePermissionChecker>
                        </Layout>
                      </GlobalLoader>
                    </ContextVerifier>
                  </RegionContextProvider>
                </LayoutBypass>
              </BearerTokenProvider>
            </Authorize>
          </SharedAppWrapper>
        </SWRConfigContextProvider>
      </LocaleContext.Provider>
    </KeyContext.Provider>
  );
}
